import {
  Button,
  Chip,
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerHeader,
  Icon,
  toast,
} from "@assetwatch/ui";
import { useRef } from "react";
import {
  useDocumentDetail,
  useDocumentDownloadUrl,
  useDocumentUploadUrl,
  useUploadDocumentVersion,
} from "../../../queries/documents";

interface DocumentDetailDrawerProps {
  documentId: number;
  customerId: string;
  facilityId: string;
  isOpen: boolean;
  onClose: () => void;
}

type CategoryTone =
  | "informative"
  | "brand"
  | "negative"
  | "warning"
  | "neutral";

const getCategoryTone = (categoryName?: string): CategoryTone => {
  if (!categoryName) return "neutral";
  switch (categoryName.toLowerCase()) {
    case "manual":
      return "informative";
    case "procedure":
      return "brand";
    case "health & safety":
      return "negative";
    case "drawings":
      return "brand";
    case "qualifications":
      return "warning";
    default:
      return "neutral";
  }
};

export const DocumentDetailDrawer = ({
  documentId,
  customerId,
  facilityId,
  isOpen,
  onClose,
}: DocumentDetailDrawerProps) => {
  const { data: detail } = useDocumentDetail(
    documentId,
    customerId,
    facilityId,
    isOpen,
  );
  const downloadMutation = useDocumentDownloadUrl();
  const uploadUrlMutation = useDocumentUploadUrl();
  const versionMutation = useUploadDocumentVersion();
  const versionInputRef = useRef<HTMLInputElement>(null);

  const handleVersionSelected = async (file: File) => {
    try {
      const upload = await uploadUrlMutation.mutateAsync({
        customerId,
        facilityId,
        file,
        assetIds: detail?.associatedAssets.map((asset) => asset.assetId) ?? [],
      });
      const response = await fetch(upload.uploadUrl, {
        method: "PUT",
        headers: { "Content-Type": file.type },
        body: file,
      });
      if (!response.ok) throw new Error(`S3 upload failed: ${response.status}`);
      await versionMutation.mutateAsync({
        customerId,
        facilityId,
        documentId,
        fileId: upload.fileId,
      });
    } catch (error) {
      console.error("Failed to upload document version:", error);
      toast.error("Failed to upload document version");
    }
  };

  if (!detail) return null;

  return (
    <Drawer open={isOpen} onOpenChange={onClose}>
      <DrawerContent>
        <DrawerHeader title={detail.document.DocumentName} />
        <DrawerBody>
          <div className="flex flex-col gap-6">
            <div className="flex items-start justify-between">
              <div>
                <h3 className="text-lg font-semibold">
                  {detail.document.DocumentName}
                </h3>
                <p className="text-sm text-muted">
                  {detail.document.DocumentNumber}
                </p>
                <Chip
                  tone={
                    detail.document.StatusCode === "Published"
                      ? "positive"
                      : "neutral"
                  }
                  label={detail.document.StatusCode}
                />
              </div>
              <Button variant="outline" onClick={onClose} size="sm">
                <Icon name="circle-xmark-solid" />
              </Button>
            </div>

            {/* Classification Section */}
            {detail.category && (
              <DetailSection title="Classification">
                <Chip
                  tone={getCategoryTone(detail.category.categoryName)}
                  label={detail.category.categoryName}
                />
              </DetailSection>
            )}

            {/* Metadata Section */}
            <DetailSection title="Document Info">
              <DetailGrid>
                <DetailField
                  label="Owner"
                  value={
                    <div className="flex items-center gap-2">
                      <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary text-white text-xs font-semibold">
                        {detail.owner.name.charAt(0)}
                      </div>
                      <span>{detail.owner.name}</span>
                    </div>
                  }
                />
                <DetailField
                  label="Status"
                  value={
                    <Chip
                      tone={
                        detail.document.StatusCode === "Published"
                          ? "positive"
                          : "neutral"
                      }
                      label={detail.document.StatusCode}
                    />
                  }
                />
                <DetailField
                  label="File Size"
                  value={formatFileSize(detail.fileSize)}
                />
                <DetailField
                  label="Uploaded"
                  value={new Date(
                    detail.document.DateCreated,
                  ).toLocaleDateString()}
                />
                <DetailField
                  label="Last Updated"
                  value={new Date(
                    detail.document.DateUpdated,
                  ).toLocaleDateString()}
                />
              </DetailGrid>
            </DetailSection>

            {/* Associated Assets Section */}
            <DetailSection title="Associated Assets (1)">
              {detail.associatedAssets.length > 0 ? (
                <div className="flex flex-col gap-2">
                  {detail.associatedAssets.map((asset) => (
                    <div
                      key={asset.assetId}
                      className="flex items-center gap-2 rounded border border-border p-2"
                    >
                      <div className="flex-1">
                        <p className="text-sm font-medium">{asset.assetName}</p>
                        <p className="text-xs text-muted">
                          {asset.assetNumber}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted">No associated assets</p>
              )}
            </DetailSection>

            {/* Version History Section */}
            <DetailSection title="Version History">
              <div className="flex flex-col gap-2">
                {detail.versions.map((version) => (
                  <div
                    key={version.versionNumber}
                    className="rounded border border-border p-3"
                  >
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="font-medium">v{version.versionNumber}</p>
                        <p className="text-xs text-muted">
                          {version.uploadedBy} · {version.uploadedDate}
                        </p>
                      </div>
                    </div>
                    {version.notes && (
                      <p className="mt-1 text-xs">{version.notes}</p>
                    )}
                  </div>
                ))}
              </div>
            </DetailSection>

            {/* Actions Footer - future roadmap items */}
            <div className="flex flex-row gap-2 border-t border-border pt-4">
              <div className="flex-1">
                <Button
                  variant="primary"
                  onClick={async () => {
                    const result = await downloadMutation.mutateAsync({
                      customerId,
                      facilityId,
                      documentId,
                    });
                    window.open(
                      result.downloadUrl,
                      "_blank",
                      "noopener,noreferrer",
                    );
                  }}
                  disabled={downloadMutation.isPending}
                >
                  <Icon name="download" />
                  Open document
                </Button>
              </div>
              <div className="flex-1">
                <input
                  ref={versionInputRef}
                  type="file"
                  accept="application/pdf,.docx,.xlsx"
                  className="hidden"
                  onChange={(event) => {
                    const file = event.target.files?.[0];
                    event.target.value = "";
                    if (file) void handleVersionSelected(file);
                  }}
                />
                <Button
                  variant="outline"
                  onClick={() => versionInputRef.current?.click()}
                  disabled={
                    uploadUrlMutation.isPending || versionMutation.isPending
                  }
                >
                  <Icon name="document-upload" />
                  Upload new version
                </Button>
              </div>
            </div>
          </div>
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
};

interface DetailSectionProps {
  title: string;
  children: React.ReactNode;
}

const DetailSection = ({ title, children }: DetailSectionProps) => (
  <div className="flex flex-col gap-3">
    <h3 className="font-semibold">{title}</h3>
    {children}
  </div>
);

interface DetailGridProps {
  children: React.ReactNode;
}

const DetailGrid = ({ children }: DetailGridProps) => (
  <div className="grid grid-cols-2 gap-4">{children}</div>
);

interface DetailFieldProps {
  label: string;
  value: React.ReactNode;
}

const DetailField = ({ label, value }: DetailFieldProps) => (
  <div className="flex flex-col gap-1">
    <span className="text-xs font-medium uppercase text-muted">{label}</span>
    <div>{value}</div>
  </div>
);

function formatFileSize(bytes: number): string {
  if (bytes === 0) return "0 B";
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${Math.round((bytes / k ** i) * 10) / 10} ${sizes[i]}`;
}
